import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setTimezone } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache duration for shop name (24 hours)
const SHOP_NAME_CACHE_DURATION = 24 * 60 * 60 * 1000;
// Cache duration for timezone (1 hour)
const TIMEZONE_CACHE_DURATION = 60 * 60 * 1000;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [adminCreated, setAdminCreated] = useState(null);
  
  // Location state for multi-location support
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(localStorage.getItem('selectedLocationId') || null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  
  // Shop name for white-labeling
  const [shopName, setShopName] = useState(() => {
    // Load from cache on init
    const cached = localStorage.getItem('shopNameCache');
    if (cached) {
      try {
        const { name, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < SHOP_NAME_CACHE_DURATION) {
          return name;
        }
      } catch (e) {
        console.error('Failed to parse shop name cache');
      }
    }
    return null;
  });

  // Set up axios interceptor for auth header
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  // Load locations from Shopify
  const loadLocations = useCallback(async () => {
    if (!token) return;
    
    setLocationsLoading(true);
    try {
      const response = await axios.get(`${API}/api/shopify/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedLocations = response.data.locations || [];
      setLocations(fetchedLocations);
      
      // If no location selected, set default based on user's default_location_id or primary location
      if (!selectedLocationId && fetchedLocations.length > 0) {
        // Priority: user's default location > primary location > first location
        let defaultLoc = fetchedLocations.find(l => l.is_primary) || fetchedLocations[0];
        if (user?.default_location_id) {
          const userDefault = fetchedLocations.find(l => l.id === user.default_location_id);
          if (userDefault) defaultLoc = userDefault;
        }
        setSelectedLocationId(defaultLoc.id);
        localStorage.setItem('selectedLocationId', defaultLoc.id);
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  }, [token, selectedLocationId, user?.default_location_id]);
  
  // Load shop name from Shopify (for white-labeling)
  const loadShopName = useCallback(async () => {
    if (!token) return;
    
    // Check if we have a valid cached name
    const cached = localStorage.getItem('shopNameCache');
    if (cached) {
      try {
        const { name, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < SHOP_NAME_CACHE_DURATION && name) {
          setShopName(name);
          return;
        }
      } catch (e) {
        console.error('Failed to parse shop name cache');
      }
    }
    
    // Fetch from API
    try {
      const response = await axios.get(`${API}/api/shopify/test`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const name = response.data.shop_name;
      if (name) {
        setShopName(name);
        // Cache for 24 hours
        localStorage.setItem('shopNameCache', JSON.stringify({
          name,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to load shop name:', error);
    }
  }, [token]);

  // Load system timezone from settings (for global timezone enforcement)
  const loadSystemTimezone = useCallback(async () => {
    if (!token) return;
    
    // Check if we have a valid cached timezone
    const cached = localStorage.getItem('timezoneCache');
    if (cached) {
      try {
        const { timezone, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < TIMEZONE_CACHE_DURATION && timezone) {
          setTimezone(timezone);
          return;
        }
      } catch (e) {
        console.error('Failed to parse timezone cache');
      }
    }
    
    // Fetch from settings API
    try {
      const response = await axios.get(`${API}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const timezone = response.data.system_timezone;
      if (timezone) {
        setTimezone(timezone);
        // Cache for 1 hour
        localStorage.setItem('timezoneCache', JSON.stringify({
          timezone,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to load system timezone:', error);
      // Default to America/Toronto if settings cannot be loaded
      setTimezone('America/Toronto');
    }
  }, [token]);

  // Load user on mount if token exists
  const loadUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Token is invalid, clear it
      localStorage.removeItem('token');
      localStorage.removeItem('selectedLocationId');
      setToken(null);
      setUser(null);
      setSelectedLocationId(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Load locations, shop name, and timezone after user is loaded
  useEffect(() => {
    if (user && token) {
      loadLocations();
      loadShopName();
      loadSystemTimezone();
    }
  }, [user, token, loadLocations, loadShopName, loadSystemTimezone]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Check if admin was just created
      if (userData.admin_created) {
        setAdminCreated(userData.admin_created);
      }

      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // IP allowlist gate: backend returns a 403 with a structured detail and a
      // short-lived ip_request token the lock screen uses to request approval.
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'ip_not_approved') {
        return {
          success: false,
          ipNotApproved: true,
          ip: detail.ip,
          ipRequestToken: detail.ip_request_token,
          error: detail.message || "This device's network hasn't been approved yet.",
        };
      }
      const message = (typeof detail === 'string' ? detail : detail?.message) || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Wipe ALL session artefacts (including a stashed impersonator
      // token, so you can't "switch back" after a real logout).
      localStorage.removeItem('token');
      localStorage.removeItem('originalAuthToken');
      localStorage.removeItem('selectedLocationId');
      setToken(null);
      setUser(null);
      setSelectedLocationId(null);
      setLocations([]);
    }
  };

  // ============ Impersonation ============
  // The manager hits "Login as" → we stash their current token under
  // `originalAuthToken`, swap to the freshly-minted target token, and
  // refresh. `stopImpersonating` reverses it: writes an audit log entry
  // then restores the stashed token. The yellow "Viewing as …" banner
  // is driven by `user.impersonator_id` returned from /auth/me.
  const startImpersonating = async (userId) => {
    try {
      const res = await axios.post(
        `${API}/api/staff/${userId}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newToken = res.data.access_token;
      // Stash the current (admin) token so "Switch back" can restore it
      // without forcing a re-login.
      localStorage.setItem('originalAuthToken', token);
      localStorage.setItem('token', newToken);
      // Drop selected-location cache so the target's default applies.
      localStorage.removeItem('selectedLocationId');
      // Hard reload to flush any in-memory state cached against the old
      // token (cart, batch drafts, location, etc.) — much safer than
      // trying to surgically reset 20+ React contexts.
      window.location.assign('/');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start impersonation';
      return { success: false, error: message };
    }
  };

  const stopImpersonating = async () => {
    const original = localStorage.getItem('originalAuthToken');
    if (!original) {
      // Defensive: nothing to switch back to → fall back to logout.
      return logout();
    }
    try {
      // Best-effort audit log; we don't block the swap on failure.
      await axios.post(`${API}/api/staff/impersonate/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.warn('Stop impersonation audit log failed:', e);
    }
    localStorage.setItem('token', original);
    localStorage.removeItem('originalAuthToken');
    localStorage.removeItem('selectedLocationId');
    window.location.assign('/settings');
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  };

  const isAdmin = () => user?.role === 'Admin';
  const isManager = () => user?.role === 'Manager' || user?.role === 'Admin';

  // Check if user can access a specific location
  const canAccessLocation = (locationId) => {
    if (!user) return false;
    // Admin and Manager with no restrictions can access all
    if ((user.role === 'Admin' || user.role === 'Manager') && !user.allowed_location_ids) {
      return true;
    }
    // Check allowed locations list
    if (user.allowed_location_ids && user.allowed_location_ids.length > 0) {
      return user.allowed_location_ids.includes(locationId);
    }
    // Cashiers can only access their default location
    if (user.default_location_id) {
      return locationId === user.default_location_id;
    }
    return false;
  };

  // Get locations user can access
  const getAccessibleLocations = () => {
    if (!user) return [];
    // Admin and Manager with no restrictions can access all
    if ((user.role === 'Admin' || user.role === 'Manager') && !user.allowed_location_ids) {
      return locations;
    }
    // Filter by allowed locations
    if (user.allowed_location_ids && user.allowed_location_ids.length > 0) {
      return locations.filter(l => user.allowed_location_ids.includes(l.id));
    }
    // Cashiers can only access their default location
    if (user.default_location_id) {
      return locations.filter(l => l.id === user.default_location_id);
    }
    return [];
  };

  // Change selected location
  const selectLocation = (locationId) => {
    if (canAccessLocation(locationId)) {
      setSelectedLocationId(locationId);
      localStorage.setItem('selectedLocationId', locationId);
    }
  };

  // Get selected location object
  const getSelectedLocation = () => {
    return locations.find(l => l.id === selectedLocationId) || null;
  };

  const clearAdminCreatedMessage = () => setAdminCreated(null);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    adminCreated,
    clearAdminCreatedMessage,
    login,
    logout,
    startImpersonating,
    stopImpersonating,
    isImpersonating: !!user?.impersonator_id,
    impersonatorName: user?.impersonator_name || null,
    hasPermission,
    isAdmin,
    isManager,
    refreshUser: loadUser,
    // Location exports
    locations,
    selectedLocationId,
    locationsLoading,
    selectLocation,
    canAccessLocation,
    getAccessibleLocations,
    getSelectedLocation,
    refreshLocations: loadLocations,
    // Shop name for white-labeling
    shopName,
    refreshShopName: loadShopName
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
